import { useEffect, useState } from 'react';
import { PlayCircleIcon } from '@heroicons/react/24/outline';
import { advanceWorkflow } from '../../../services/workQueue';
import { toApiError } from '../../../services/apiErrors';
import { usePatientCard } from '../../../hooks/usePatientCard';
import { useDmQuickstarts } from '../../../hooks/useDmQuickstarts';
import type { DmQuickstart } from '../../../services/dmQuickstarts';
import type { PatientMonitoringBlock } from '../../../services/patientCard';
import { useToast } from '../../../providers/ToastProvider';
import { TaskSidebarPanel } from './TaskSidebarPanel';
import { buildSidebarTabs, OPS_TABS } from './taskSidebarTabs';
import {
    DmLinkStatusChip,
    DmMonitoringStatusChip,
    DmAppActivationChip
} from '../../patient-files/actions/dmStatusChips';
import type { TaskBodyProps } from './index';

// Mirror of the backend start-monitoring validator bounds.
const MIN_ALIGNER = 1;
const MAX_ALIGNER = 200;

function isValidAligner(value: number | ''): value is number {
    return value !== '' && Number.isInteger(value) && value >= MIN_ALIGNER && value <= MAX_ALIGNER;
}

function StateRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between items-center py-1.5 gap-2">
            <span className="text-[12px] font-medium text-gray-500 flex-shrink-0">{label}</span>
            <span className="text-[12.5px] text-gray-800 truncate min-w-0">{value}</span>
        </div>
    );
}

function MonitoringStateCard({
    monitoring,
    isLoading
}: {
    monitoring: PatientMonitoringBlock | undefined;
    isLoading: boolean;
}) {
    const dmLinked = monitoring?.dmId != null;
    const status = monitoring?.monitoringStatus ?? null;
    const normalized = (status ?? '').toLowerCase();
    // 'resumed' is Monday-speak for an active monitoring: same as started.
    const started = normalized === 'started' || normalized === 'resumed';
    const paused = normalized === 'paused';
    const aligners =
        monitoring?.currentAligner || monitoring?.totalAligners
            ? `${monitoring?.currentAligner ?? '—'} / ${monitoring?.totalAligners ?? '—'}`
            : '—';

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Current monitoring state
            </p>
            {/* Same status chips as the PatientFiles Actions tab header */}
            <div className="flex flex-wrap items-center gap-1.5">
                <DmLinkStatusChip isLoading={isLoading} dmId={monitoring?.dmId ?? null} />
                {dmLinked && <DmMonitoringStatusChip status={monitoring?.monitoringStatus} />}
                {dmLinked && <DmAppActivationChip status={monitoring?.dmAppActivationStatus} />}
            </div>
            <div className="mt-2 divide-y divide-gray-100">
                <StateRow label="Aligner" value={aligners} />
                {monitoring?.dmScanFrequency && (
                    <StateRow label="Scan frequency" value={monitoring.dmScanFrequency} />
                )}
            </div>
            {!dmLinked && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                    This patient is not linked to Dental Monitoring. Set up DM from the patient card
                    before starting monitoring.
                </p>
            )}
            {started && (
                <p className="mt-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5">
                    DM already reports monitoring as started. Submitting will sync the aligner
                    numbers and advance the case.
                </p>
            )}
            {paused && (
                <p className="mt-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5">
                    Monitoring is paused. Submitting will resume it with its existing quickstart and
                    sync the aligner numbers.
                </p>
            )}
        </div>
    );
}

function QuickstartPicker({
    quickstarts,
    isLoading,
    error,
    selectedId,
    onSelect
}: {
    quickstarts: DmQuickstart[];
    isLoading: boolean;
    error: unknown;
    selectedId: string | null;
    onSelect: (id: string) => void;
}) {
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Quickstart
            </p>
            {isLoading ? (
                <p className="text-xs text-gray-400">Loading quickstarts…</p>
            ) : error ? (
                <p className="text-xs text-red-500">
                    Failed to load quickstarts from Dental Monitoring.
                </p>
            ) : quickstarts.length === 0 ? (
                <p className="text-xs text-gray-400">
                    No quickstarts available for this patient in DM.
                </p>
            ) : (
                <div className="flex flex-col gap-1.5">
                    {quickstarts.map((q) => {
                        const selected = q.id === selectedId;
                        return (
                            <button
                                key={q.id}
                                type="button"
                                onClick={() => onSelect(q.id)}
                                aria-pressed={selected}
                                className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                                    selected
                                        ? 'border-gray-900 bg-gray-50'
                                        : 'border-gray-200 bg-white hover:bg-gray-50'
                                }`}
                            >
                                <span className="flex items-center gap-2">
                                    <span
                                        className={`w-3.5 h-3.5 rounded-full border flex-shrink-0 flex items-center justify-center ${
                                            selected ? 'border-gray-900' : 'border-gray-300'
                                        }`}
                                    >
                                        {selected && (
                                            <span className="w-2 h-2 rounded-full bg-gray-900" />
                                        )}
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block text-sm font-semibold text-gray-800 truncate">
                                            {q.name}
                                        </span>
                                        {q.description && (
                                            <span className="block text-xs text-gray-500 truncate">
                                                {q.description}
                                            </span>
                                        )}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function AlignerInput({
    label,
    value,
    onChange,
    invalid,
    placeholder
}: {
    label: string;
    value: number | '';
    onChange: (value: number | '') => void;
    invalid: boolean;
    placeholder: string;
}) {
    return (
        <label className="flex-1 min-w-0">
            <span className="block text-xs font-semibold text-gray-700 mb-1">{label}</span>
            <input
                type="number"
                min={MIN_ALIGNER}
                max={MAX_ALIGNER}
                value={value}
                onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder={placeholder}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none transition-colors duration-200 ${
                    invalid
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                }`}
            />
        </label>
    );
}

/**
 * Tx 0 ("Pending Monitoring start"). GP-owned step surfaced as an internal
 * task so ODO staff can start DM monitoring on the practice's behalf: pick
 * the quickstart, set the aligner numbers, and fire the same
 * /workflow/in-treatment/start-monitoring action the dashboard uses.
 */
export function StartMonitoringTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    const { showToast } = useToast();
    const { data: card, isLoading: cardLoading } = usePatientCard(c.id);
    const { data: quickstarts, isLoading, error } = useDmQuickstarts(c.id);

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [currentAligner, setCurrentAligner] = useState<number | ''>('');
    const [totalAligner, setTotalAligner] = useState<number | ''>('');
    const [submitting, setSubmitting] = useState(false);

    // This task drives its own completion, so hide TaskSurface's action buttons.
    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: false, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Tx 0 is the initial treatment start, so preselect the "initial" quickstart
    // when DM offers one; anything else stays an explicit choice.
    useEffect(() => {
        if (selectedId !== null || quickstarts.length === 0) return;
        const initial = quickstarts.find((q) => q.name.toLowerCase().includes('initial'));
        if (initial) setSelectedId(initial.id);
    }, [quickstarts, selectedId]);

    const monitoring = card?.monitoring;
    const dmLinked = monitoring?.dmId != null;
    const mirrorStatus = (monitoring?.monitoringStatus ?? '').toLowerCase();
    const paused = mirrorStatus === 'paused';
    // A quickstart is only needed when DM monitoring will actually be started:
    // paused monitoring is resumed with its existing quickstart, and an
    // already-started one ('started', or its Monday synonym 'resumed') is left
    // alone (aligner sync only).
    const needsQuickstart = !paused && mirrorStatus !== 'started' && mirrorStatus !== 'resumed';
    const selected = quickstarts.find((q) => q.id === selectedId) ?? null;

    const currentValid = isValidAligner(currentAligner);
    const totalValid = isValidAligner(totalAligner);
    const alignersConsistent = currentValid && totalValid && totalAligner >= currentAligner;
    const canSubmit =
        dmLinked && (!needsQuickstart || selected !== null) && alignersConsistent && !submitting;

    // The step's single forward edge (external actor: the surface hides it, we own it).
    const forwardAction = statusMeta.outgoingActions.find((a) => a.direction === 'forward');

    const handleSubmit = async () => {
        if (!currentValid || !totalValid || (needsQuickstart && !selected)) return;
        setSubmitting(true);
        try {
            await advanceWorkflow(c.id, 'start-monitoring', {
                ...(needsQuickstart && selected
                    ? { quickstart: selected.id, quickstartName: selected.name }
                    : {}),
                currentAligner,
                totalAligner
            });
            onTaskComplete?.(forwardAction);
        } catch (err) {
            showToast(toApiError(err).message, 'error');
            setSubmitting(false);
        }
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 flex gap-3 px-4 pt-3 pb-3">
                {/* LEFT: monitoring state, quickstart selection, aligner numbers */}
                <div className="flex-[6] min-w-0 min-h-0 overflow-y-auto flex flex-col gap-3">
                    <MonitoringStateCard monitoring={monitoring} isLoading={cardLoading} />
                    {needsQuickstart && (
                        <QuickstartPicker
                            quickstarts={quickstarts}
                            isLoading={isLoading}
                            error={error}
                            selectedId={selectedId}
                            onSelect={setSelectedId}
                        />
                    )}
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                            Aligner numbers
                        </p>
                        <div className="flex gap-3">
                            <AlignerInput
                                label="Current aligner"
                                value={currentAligner}
                                onChange={setCurrentAligner}
                                invalid={currentAligner !== '' && !currentValid}
                                placeholder="e.g. 1"
                            />
                            <AlignerInput
                                label="Total aligners"
                                value={totalAligner}
                                onChange={setTotalAligner}
                                invalid={
                                    totalAligner !== '' &&
                                    (!totalValid || (currentValid && totalAligner < currentAligner))
                                }
                                placeholder="e.g. 24"
                            />
                        </div>
                        {currentValid && totalValid && totalAligner < currentAligner && (
                            <p className="mt-1.5 text-xs text-red-500">
                                Total aligners must be greater than or equal to the current aligner.
                            </p>
                        )}
                    </div>
                </div>

                {/* RIGHT: full-height sidebar with Patient info, Patient notes, Patient files */}
                <div className="flex-[4] min-w-0 min-h-0">
                    <TaskSidebarPanel tabs={buildSidebarTabs(c.id, OPS_TABS)} />
                </div>
            </div>

            {/* Footer: this body owns submit (the surface hides external-actor edges) */}
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-200 flex-shrink-0">
                <span className="text-xs text-gray-400 italic">
                    Performs the practice's "Start monitoring" action on their behalf.
                </span>
                <button
                    type="button"
                    onClick={() => {
                        void handleSubmit();
                    }}
                    disabled={!canSubmit}
                    className="flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <PlayCircleIcon className="w-4 h-4" />
                    {submitting
                        ? paused
                            ? 'Resuming…'
                            : 'Starting…'
                        : paused
                          ? 'Resume monitoring'
                          : 'Start monitoring'}
                </button>
            </div>
        </div>
    );
}
